import { ReactElement } from 'react';
import {
  AccountCircle as AccountIcon,
  ChevronLeft,
  Apartment,
  Groups,
  LocalPolice,
  CreditCard,
  Key,
  List as ListIcon,
  TrendingUpRounded as TrendingUpRoundedIcon,
} from '@mui/icons-material';

import { PasskeyIcon } from '@localstack/ui';

import { Permission, ProductFeature, UserRole } from '@localstack/types';

import { buildRoute } from '@localstack/services';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { NavbarContainer, NavbarSection, SectionItem, SectionHeader } from './shared';
import { NavbarCloseButton } from './NavbarCloseButton';

/** Contains menu items to be shown with the main actions of the app */
export const SettingsNavbar = (): ReactElement => {
  const { can, hasFeature, userInfo } = useAuthProvider();
  const isWorkspaceAdmin = userInfo?.roles.includes(UserRole.ADMIN);

  // show billing if user is permitted for any of those actions
  const billingPermissions = [
    Permission.READ_SUBSCRIPTIONS,
    Permission.READ_CREDIT_CARDS,
    Permission.READ_INVOICES,
    Permission.UPDATE_ORGANIZATION,
  ];
  return (
    <NavbarContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <SectionItem label="Back to app" icon={ChevronLeft} to={AppRoute.GETTING_STARTED} />
        <NavbarCloseButton />
      </div>
      <NavbarSection data-name="account sub navigation">
        <SectionHeader>Account</SectionHeader>
        <SectionItem label="Profile" to={buildRoute(AppRoute.SETTINGS_PROFILE)} end icon={AccountIcon}></SectionItem>
        <SectionItem
          label="Authentication"
          to={buildRoute(AppRoute.SETTINGS_AUTHENTICATION)}
          icon={PasskeyIcon}
        ></SectionItem>
      </NavbarSection>

      <NavbarSection data-name="administration sub navigation">
        <SectionHeader>Administration</SectionHeader>

        {can(Permission.UPDATE_ORGANIZATION) && (
          <SectionItem
            label="Workspace"
            to={buildRoute(AppRoute.SETTINGS_WORKSPACE)}
            end
            icon={Apartment}
          ></SectionItem>
        )}

        {can(Permission.READ_MEMBERS) && can(Permission.READ_SUBSCRIPTIONS) && (
          <SectionItem label="Users & Licenses" to={buildRoute(AppRoute.SETTINGS_MEMBERS)} icon={Groups}></SectionItem>
        )}

        {hasFeature(ProductFeature.FEATURE_SSO) && can(Permission.UPDATE_ORGANIZATION) && (
          <SectionItem label="Single Sign-on" to={buildRoute(AppRoute.SETTINGS_SSO)} icon={LocalPolice}></SectionItem>
        )}

        {isWorkspaceAdmin && (hasFeature(ProductFeature.FEATURE_SSO) || hasFeature(ProductFeature.FEATURE_SCIM)) && (
          <SectionItem
            label="Identity Logs"
            to={buildRoute(AppRoute.SETTINGS_IDENTITY_LOGS)}
            icon={ListIcon}
          ></SectionItem>
        )}

        {isWorkspaceAdmin && hasFeature(ProductFeature.FEATURE_USAGE_INSIGHTS) && (
          <SectionItem
            label="Usage"
            to={buildRoute(AppRoute.SETTINGS_USAGE)}
            icon={TrendingUpRoundedIcon}
          ></SectionItem>
        )}

        <SectionItem label="Auth Tokens" to={buildRoute(AppRoute.SETTINGS_AUTH_TOKENS)} end icon={Key}></SectionItem>

        {billingPermissions.some(can) && (
          <SectionItem label="Billing" to={buildRoute(AppRoute.SETTINGS_BILLING)} icon={CreditCard}></SectionItem>
        )}
      </NavbarSection>
    </NavbarContainer>
  );
};
